import h from "trader-hyperscript";
import { Source } from "trader.ts/types/source";
import { dyn, dynVariadic } from "trader.ts/ui";
import { Parsing } from "trader.ts/ui.forms";
import { tryExtractErrorMessage } from "trader.ts/utils";
import * as FA from "../../styles/fontawesome.css.classes";
import * as style from "../../styles/style.css.classes";
import { isFunction, isNil, isString } from "../lib/istype";
import { touchAllInputs } from "../lib/touchallinputs";

export function renderSaveButton<Form = null>(opts: {
  save: (form: Form) => Promise<
    | null
    | undefined
    | void
    | { error: string; link?: string | null }
    | { tag: "error"; error: string }
    | {
        tag: "success";
        text?: string | HTMLElement;
        link?: string | null;
        redirectUrl?: string;
        timeout?: number;
      }
  >;
  text?: {
    normal?: string;
    success?: string;
    loading?: string;
  };
  reload: boolean | ((form: Form) => void);
  formS?: Source<Parsing<Form>>;
  timeout?: number;
  style?: string;
  smallbuttons?: boolean;
  disabled?: boolean | string | ((f: Form) => string | boolean);
  icon?: string;
}): HTMLElement[] {
  const loadingS = new Source(false);
  const savedS: Source<null | {
    text: HTMLElement | string | null;
    link: string | null;
    willReload: boolean;
  }> = new Source(null);
  const errorS: Source<null | { error: HTMLElement }> = new Source(null);
  const formS = (opts.formS ||
    new Source({
      tag: "parsed",
      parsed: null,
    } as Parsing<null>)) as Source<Parsing<Form>>;
  const didScrollIntoViewErrorS = new Source(false);
  const timeoutMs = opts.timeout || 750;

  function doSave(f: Form) {
    touchAllInputs();
    didScrollIntoViewErrorS.set(false);
    errorS.set(null);
    loadingS.set(true);
    opts
      .save(f)
      .then((res) => {
        if (res && "error" in res) {
          errorS.set({ error: renderErrorPanel(res) });
        } else {
          errorS.set(null);
          savedS.set(
            res && "tag" in res && res.tag === "success"
              ? {
                  text: res.text || null,
                  link: res.link || null,
                  willReload: !isNil(res.redirectUrl) || opts.reload !== false,
                }
              : { text: null, link: null, willReload: false },
          );

          setTimeout(
            function () {
              savedS.set(null);
              if (res?.redirectUrl) {
                document.location = res.redirectUrl;
              } else if (opts.reload === true) {
                document.location.reload();
              } else if (opts.reload === false) {
                //nothing
              } else {
                opts.reload(f);
              }
            },
            res?.timeout || timeoutMs || 1500,
          );
        }
      })
      .catch((err) => {
        errorS.set({
          error: renderErrorPanel({ error: tryExtractErrorMessage(err) }),
        });
      })
      .finally(() => loadingS.set(false));
  }

  return [
    ...dynVariadic([savedS, loadingS, formS], ([saved, loading, form]) => {
      if (form && form.tag !== "parsed") {
        return (
          <button
            className={
              style.button_em_down +
              (opts.smallbuttons ? style.button_small : "")
            }
            style={opts.style || ""}
            onclick={() => {
              touchAllInputs();
              errorS.set({
                error: renderErrorPanel({
                  error: "Een of meer velden zijn nog niet in orde",
                }),
              });
            }}
          >
            <i className={opts.icon || FA.icon_save_solid}></i>
            {opts.text?.normal || "Sla op"}
          </button>
        );
      } else {
        if (saved === null) {
          if (loading === true) {
            const loadingText = opts.text?.loading || null;
            return loadingText === null ? (
              <button
                className={
                  style.button_accent +
                  style.loading +
                  (opts.smallbuttons ? style.button_small : "")
                }
                style={opts.style || ""}
              ></button>
            ) : (
              <button
                className={
                  style.button_accent +
                  (opts.smallbuttons ? style.button_small : "")
                }
                style={(opts.style || "") + "; display: inline-flex;"}
              >
                <div className={style.loading}></div>
                <div style="margin-left: 16px;">{loadingText}</div>
              </button>
            );
          } else {
            const disabledVal = isFunction(opts.disabled)
              ? opts.disabled(form.parsed)
              : opts.disabled;
            if (!isNil(disabledVal) && disabledVal !== false) {
              const errString =
                isString(disabledVal) && disabledVal.length > 0
                  ? disabledVal
                  : "Een of meer velden zijn nog niet in orde";
              return (
                <button
                  className={
                    style.button_em_down +
                    (opts.smallbuttons ? style.button_small : "")
                  }
                  style={opts.style || ""}
                  onclick={() => {
                    touchAllInputs();
                    errorS.set({
                      error: renderErrorPanel({
                        error: errString,
                      }),
                    });
                  }}
                >
                  <i className={opts.icon || FA.icon_save_solid}></i>
                  {opts.text?.normal || "Sla op"}
                </button>
              );
            } else {
              return (
                <button
                  className={
                    style.button_accent +
                    (opts.smallbuttons ? style.button_small : "")
                  }
                  style={opts.style || ""}
                  onclick={() => doSave(form.parsed)}
                >
                  <i className={opts.icon || FA.icon_save_solid}></i>
                  {opts.text?.normal || "Sla op"}
                </button>
              );
            }
          }
        } else {
          // Pretty big mess here :(
          const showReloadAnimation =
            saved.willReload === true && timeoutMs < 30000;

          const button = (
            <button
              className={
                style.button_green +
                (showReloadAnimation ? style.animate_saved_state : "") +
                (opts.smallbuttons ? style.button_small : "")
              }
              style={
                (opts.style || "") +
                (showReloadAnimation
                  ? `; animation-duration: ${timeoutMs}ms;`
                  : "")
              }
            >
              <i className={FA.icon_check_solid}></i>
              {isString(saved.text)
                ? saved.text
                : opts.text?.success || "Opgeslagen!"}
            </button>
          );
          const buttonWithLink = saved.link ? (
            <a href={saved.link}>{button}</a>
          ) : (
            button
          );
          const panel =
            saved.text && !isString(saved.text)
              ? renderSuccessPanel(saved.text)
              : null;
          const panelWithLink = panel ? (
            saved.link ? (
              <a href={saved.link}>{panel}</a>
            ) : (
              panel
            )
          ) : (
            <span></span>
          );
          return [buttonWithLink, panelWithLink];
        }
      }
    }),
    ...dynVariadic(
      [errorS, didScrollIntoViewErrorS],
      ([err, didScrollIntoViewError]) => {
        const errElement = err ? (
          isString(err.error) ? (
            <div style="color: red" className={style.text_with_icon}>
              <i
                className={FA.icon_exclamation_circle_solid}
                style="margin-right: 12px"
              ></i>
              {err.error}
            </div>
          ) : (
            err.error
          )
        ) : null;
        if (errElement && didScrollIntoViewError === false) {
          setTimeout(() => {
            errElement.scrollIntoView({ block: "nearest" });
            didScrollIntoViewErrorS.set(true);
          }, 0);
        }
        return errElement || [];
      },
    ),
  ];
}

export function renderErrorPanel(err: { error: string; link?: string | null }) {
  const st = `
  background-color: #ffe8e8;
  border: 1px solid red;
  margin-top: 8px;
  text-align: center;
  word-break: break-word;
`;

  return (
    <div style={st} className={style.panel}>
      {err.error}.
      {err.link ? (
        <div>
          <a href={err.link} target="_blank" rel="noopener noreferrer">
            <b>{"Bekijk hier"}</b>
          </a>
        </div>
      ) : (
        <span></span>
      )}
    </div>
  );
}

export function renderSuccessPanel(text: HTMLElement | string) {
  const st = `
  background-color: rgb(238 255 238);
  border: 1px solid green;
  margin-top: 8px;
  text-align: center;
  word-break: break-word;
`;

  return (
    <div style={st} className={style.panel}>
      {text}
    </div>
  );
}

export function renderCancelButton(opts: {
  onclick: () => unknown;
  smallbuttons?: boolean;
  label?: string;
  style?: string;
}) {
  return (
    <button
      className={opts.smallbuttons ? style.button_small : style.button_em_down}
      style={opts.style || ""}
      onclick={opts.onclick}
    >
      <i className={FA.icon_undo_solid}></i>
      {opts.label || "Annuleer"}
    </button>
  );
}

export function renderDeleteButton<T>(opts: {
  label: string | HTMLElement;
  confirmationLabel: string;
  doDelete: () => Promise<T>;
  redirectTo: string;
  dontShowLabelAgainInConfirmation?: true;
  className?: string;
  style?: string;
  panelStyle?: string;
  containerStyle?: string;
}): HTMLElement | HTMLElement[] {
  const deletingS = new Source(false);
  const delButton = () => (
    <button
      className={style.button + style.button_em_down + (opts.className || "")}
      style={opts.style || ""}
      onclick={() => deletingS.set(true)}
    >
      {opts.label}
    </button>
  );
  const errorS: Source<null | { error: HTMLElement }> = new Source(null);
  return dynVariadic([deletingS], function ([deleting]) {
    if (deleting) {
      const errElement = dynVariadic([errorS], function ([err]) {
        return err ? (
          isString(err.error) ? (
            <div style="color: red" className={style.text_with_icon}>
              <i
                className={FA.icon_exclamation_circle_solid}
                style="margin-right: 12px"
              ></i>
              {err.error}
            </div>
          ) : (
            err.error
          )
        ) : (
          <span></span>
        );
      });
      const deleteLoadingS = new Source(false);
      return (
        <div className={style.delete_panel} style={opts.containerStyle || ""}>
          {delButton()}
          <div
            className={style.panel + style.slide_in}
            style={"margin-top: 8px;" + (opts.panelStyle || "")}
          >
            <article>{opts.confirmationLabel}</article>
            <div style="text-align: right">
              <button
                className={style.button_em_down}
                onclick={() => deletingS.set(false)}
              >
                {"Annuleer"}
              </button>
              {dyn(deleteLoadingS, (deleteLoading) => {
                if (deleteLoading === false) {
                  const label = opts.dontShowLabelAgainInConfirmation
                    ? "OK"
                    : opts.label;
                  const doDelete = async () => {
                    errorS.set(null);
                    deleteLoadingS.set(true);
                    await opts.doDelete().catch((err: unknown) => {
                      errorS.set({
                        error: renderErrorPanel({
                          error: tryExtractErrorMessage(err),
                        }),
                      });
                      deleteLoadingS.set(false);
                      return Promise.reject(
                        err instanceof Error
                          ? err
                          : new Error(tryExtractErrorMessage(err)),
                      );
                    });
                    document.location = opts.redirectTo;
                  };
                  return <button onclick={doDelete}>{label}</button>;
                } else {
                  return <button className={style.loading}></button>;
                }
              })}
            </div>
            {errElement}
          </div>
        </div>
      );
    } else {
      return delButton();
    }
  });
}
